import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Drawer,
  Button,
  Space,
  Tooltip,
  Tag,
  Typography,
  Segmented,
  Input,
  message,
} from 'antd';
import {
  CloseOutlined,
  CopyOutlined,
  CheckSquareOutlined,
  EditOutlined,
  RobotOutlined,
  DownloadOutlined,
  FileTextOutlined,
  CodeOutlined,
  SearchOutlined,
  UnorderedListOutlined,
  PartitionOutlined,
  AimOutlined,
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github.css';
import { colors } from '@/theme/tokens';
import { useStore } from '@/stores';

const { Text, Title } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// InstructionDetailDrawer — read-only deep-dive on a long prompt body
// ─────────────────────────────────────────────────────────────────────────────
//
// PURPOSE
//   The agent's primary instruction is typically 12–30 KB of Markdown — too
//   much to read inline on the Instructions tab. Earlier the inline panel
//   had a "Show full" button that toggled in-place expansion; for instructions
//   this size the page becomes an awkward double-scroll surface. This drawer
//   replaces the inline expand: 90% width, two columns (body + outline), and
//   exposes the operator-grade toolbar (Edit / Copy / Review with AI /
//   Download).
//
// LAYOUT
//   ┌─────────────────────────────────────────────────────────────────┐
//   │  Sticky header — agent name · "Primary instruction" · stats     │
//   │  Sticky toolbar — Edit / Copy / Review with AI / Download       │
//   ├─────────────────────────────────────────────────┬───────────────┤
//   │  Body                                            │  Outline      │
//   │   • Rendered (Markdown)  /  Raw (monospace+line  │   • # heading │
//   │     numbers) toggle                              │   • ## sub    │
//   │   • Optional search-highlight                    │  Stats card   │
//   └─────────────────────────────────────────────────┴───────────────┘
//
// REUSE
//   Used today for ai.AiAgent.Instructions. The same drawer can host any
//   long-form prompt body — pass any string; the outline auto-derives.
//
// REVIEW HOOK
//   The "Review with AI" toolbar button hands the parent control via the
//   onReviewWithAI callback. The parent owns the PromptReviewDrawer mount
//   so we don't duplicate review-state management here.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
  /** The body to render (raw text — typically Markdown). */
  body: string;
  /** Header label shown above the title (e.g. agent name). */
  contextLabel?: string | null;
  /** Stable identifier shown as a code pill (e.g. "ai.AiAgent.Instructions"). */
  stableId?: string | null;
  /** Filename used for the Download .md button. */
  downloadFilename?: string;
  /** When provided, renders the Edit button. */
  onEdit?: () => void;
  /** When provided, renders the Review-with-AI button. */
  onReviewWithAI?: () => void;
}

interface OutlineEntry {
  level: number;
  text: string;
  /** DOM id used to scroll-into-view from the outline. */
  anchorId: string;
}

export function InstructionDetailDrawer({
  open,
  onClose,
  body,
  contextLabel,
  stableId,
  downloadFilename,
  onEdit,
  onReviewWithAI,
}: Props) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [view, setView] = useState<'rendered' | 'raw'>('rendered');
  const [search, setSearch] = useState('');
  const [copied, setCopied] = useState(false);
  const bodyScrollRef = useRef<HTMLDivElement | null>(null);

  // Reset view-state when reopening so the user always lands in the
  // default mode without state leaking from a prior session.
  useEffect(() => {
    if (!open) return;
    setView('rendered');
    setSearch('');
    setCopied(false);
  }, [open]);

  const stats = useMemo(() => {
    const chars = body.length;
    const lines = body.split('\n').length;
    const tokens = Math.round(chars / 4);
    return { chars, lines, tokens };
  }, [body]);

  // Outline derivation — walk the lines, pick `# Heading` and `## Heading`
  // and `### Heading` (ignoring deeper levels to keep the outline tight).
  // anchorId mirrors the slugify rules ReactMarkdown uses by default so
  // clicking an entry jumps to the rendered heading.
  const outline = useMemo<OutlineEntry[]>(() => {
    const entries: OutlineEntry[] = [];
    const lines = body.split('\n');
    const seen = new Map<string, number>();
    let inFence = false;
    for (const raw of lines) {
      const line = raw;
      // Skip code-fenced blocks so we don't mis-treat `# comment` inside fences.
      if (/^\s*```/.test(line)) {
        inFence = !inFence;
        continue;
      }
      if (inFence) continue;
      const m = /^(\#{1,3})\s+(.+?)\s*$/.exec(line);
      if (!m) continue;
      const level = m[1].length;
      const text = m[2].trim();
      const slug = slugify(text);
      const dupeIdx = seen.get(slug) ?? 0;
      seen.set(slug, dupeIdx + 1);
      const anchorId = dupeIdx === 0 ? slug : `${slug}-${dupeIdx}`;
      entries.push({ level, text, anchorId });
    }
    return entries;
  }, [body]);

  function copy() {
    navigator.clipboard
      .writeText(body)
      .then(() => {
        setCopied(true);
        message.success('Copied to clipboard');
        setTimeout(() => setCopied(false), 1800);
      })
      .catch(() => message.error('Copy failed — clipboard blocked'));
  }

  function download() {
    const filename = downloadFilename || 'instruction.md';
    const blob = new Blob([body], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename.endsWith('.md') ? filename : `${filename}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function jumpTo(anchorId: string) {
    const el = bodyScrollRef.current?.querySelector<HTMLElement>(`#${cssEscape(anchorId)}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // Brief highlight pulse so the eye finds the target.
    el.style.transition = 'background-color 350ms ease';
    const original = el.style.backgroundColor;
    el.style.backgroundColor = isDark ? colors.violet[700] + '55' : colors.violet[100];
    setTimeout(() => {
      el.style.backgroundColor = original;
    }, 900);
  }

  // Drawer surface colors. We keep the toolbar separate from the body so
  // it can stick on scroll and read as a distinct surface.
  const surface = isDark ? colors.slate[800] : '#FFFFFF';
  const surfaceMuted = isDark ? colors.slate[900] : colors.slate[50];
  const border = isDark ? colors.slate[700] : colors.slate[200];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      width="90%"
      closable={false}
      destroyOnClose
      styles={{
        body: { padding: 0, background: surface },
        header: { display: 'none' },
      }}
    >
      {/* ── Sticky header + toolbar ────────────────────────────────────── */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: surface,
          borderBottom: `1px solid ${border}`,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 24px 10px 24px',
            gap: 16,
          }}
        >
          <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {contextLabel && (
              <Text
                type="secondary"
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                }}
              >
                {contextLabel}
              </Text>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <Title level={4} style={{ margin: 0, color: isDark ? colors.slate[50] : colors.slate[900] }}>
                <FileTextOutlined style={{ marginRight: 8, color: colors.violet[600] }} />
                Primary instruction
              </Title>
              {stableId && (
                <Tag
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    fontFamily: "'JetBrains Mono', monospace",
                    background: isDark ? colors.violet[700] + '55' : colors.violet[100],
                    color: isDark ? colors.violet[100] : colors.violet[700],
                    border: 'none',
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                  }}
                >
                  {stableId}
                </Tag>
              )}
              <StatBadge label={stats.chars.toLocaleString()} hint="chars" />
              <StatBadge label={`≈ ${stats.tokens.toLocaleString()}`} hint="tokens" />
              <StatBadge label={stats.lines.toLocaleString()} hint="lines" />
            </div>
          </div>
          <Tooltip title="Close (Esc)">
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={onClose}
              size="large"
              aria-label="Close drawer"
            />
          </Tooltip>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 24px 12px 24px',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <Space size={8} wrap>
            {onEdit && (
              <Button type="primary" icon={<EditOutlined />} onClick={onEdit}>
                Edit with AI co-pilot
              </Button>
            )}
            {onReviewWithAI && (
              <Tooltip title="Run the prompt-review agent against this body — scores 11 dimensions and proposes a rewrite if anything is below target.">
                <Button icon={<RobotOutlined />} onClick={onReviewWithAI}>
                  Review with AI
                </Button>
              </Tooltip>
            )}
            <Button
              icon={copied ? <CheckSquareOutlined /> : <CopyOutlined />}
              onClick={copy}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
            <Button icon={<DownloadOutlined />} onClick={download}>
              Download .md
            </Button>
          </Space>
          <Space size={10} wrap>
            <Segmented
              value={view}
              onChange={(v) => setView(v as 'rendered' | 'raw')}
              options={[
                { label: <span><FileTextOutlined /> Rendered</span>, value: 'rendered' },
                { label: <span><CodeOutlined /> Raw</span>, value: 'raw' },
              ]}
              size="small"
            />
            <Input
              size="small"
              placeholder="Find in instruction…"
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              style={{ width: 220 }}
            />
          </Space>
        </div>
      </div>

      {/* ── Body: two columns (content | outline) ──────────────────────── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 3fr) 320px',
          gap: 0,
          minHeight: 'calc(100vh - 138px)',
        }}
      >
        {/* Content column */}
        <div
          ref={bodyScrollRef}
          style={{
            overflow: 'auto',
            padding: '20px 24px 64px 24px',
            background: surface,
          }}
        >
          {view === 'rendered' ? (
            <RenderedBody body={body} search={search} isDark={isDark} />
          ) : (
            <RawBody body={body} search={search} isDark={isDark} />
          )}
        </div>

        {/* Right rail */}
        <aside
          style={{
            background: surfaceMuted,
            borderLeft: `1px solid ${border}`,
            padding: '20px 18px 24px 18px',
            overflow: 'auto',
            position: 'sticky',
            top: 138,
            height: 'calc(100vh - 138px)',
          }}
        >
          <RailHeader icon={<UnorderedListOutlined />} label="Outline" count={outline.length} />
          {outline.length === 0 ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              No headings detected in this instruction.
            </Text>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {outline.map((o, i) => (
                <button
                  key={`${o.anchorId}-${i}`}
                  onClick={() => jumpTo(o.anchorId)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    padding: '4px 6px 4px',
                    paddingLeft: 6 + (o.level - 1) * 14,
                    fontSize: o.level === 1 ? 13 : 12,
                    fontWeight: o.level === 1 ? 600 : 400,
                    color: isDark ? colors.slate[200] : colors.slate[700],
                    borderRadius: 6,
                    lineHeight: 1.45,
                    transition: 'background-color 120ms ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = isDark ? colors.slate[700] : colors.slate[100];
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  {o.level >= 2 && (
                    <span style={{ opacity: 0.5, marginRight: 4 }}>·</span>
                  )}
                  {o.text}
                </button>
              ))}
            </div>
          )}

          <div style={{ height: 18 }} />
          <RailHeader icon={<AimOutlined />} label="At a glance" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <RailStat label="Chars" value={stats.chars.toLocaleString()} isDark={isDark} />
            <RailStat label="Tokens" value={`≈ ${stats.tokens.toLocaleString()}`} isDark={isDark} />
            <RailStat label="Lines" value={stats.lines.toLocaleString()} isDark={isDark} />
            <RailStat label="Headings" value={String(outline.length)} isDark={isDark} />
          </div>

          {onReviewWithAI && (
            <>
              <div style={{ height: 18 }} />
              <RailHeader icon={<PartitionOutlined />} label="Quality check" />
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>
                Run the prompt-review agent — scores 11 dimensions including
                clinical accuracy + safety, returns a suggested rewrite when
                anything is below target.
              </Text>
              <Button
                block
                type="primary"
                icon={<RobotOutlined />}
                onClick={onReviewWithAI}
              >
                Review with AI
              </Button>
            </>
          )}
        </aside>
      </div>
    </Drawer>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function StatBadge({ label, hint }: { label: string; hint: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: 4,
        fontSize: 12,
        fontFamily: "'JetBrains Mono', monospace",
        color: colors.slate[600],
      }}
    >
      <strong style={{ color: colors.slate[800], fontWeight: 700 }}>{label}</strong>
      <span style={{ fontSize: 10, color: colors.slate[500] }}>{hint}</span>
    </span>
  );
}

function RailHeader({ icon, label, count }: { icon: React.ReactNode; label: string; count?: number }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        marginBottom: 8,
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: colors.slate[500],
      }}
    >
      <span style={{ fontSize: 12 }}>{icon}</span>
      <span>{label}</span>
      {count != null && (
        <span
          style={{
            background: colors.slate[100],
            color: colors.slate[700],
            borderRadius: 999,
            padding: '0 6px',
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: 0,
          }}
        >
          {count}
        </span>
      )}
    </div>
  );
}

function RailStat({ label, value, isDark }: { label: string; value: string; isDark: boolean }) {
  return (
    <div
      style={{
        background: isDark ? colors.slate[800] : '#FFFFFF',
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        borderRadius: 8,
        padding: '8px 10px',
      }}
    >
      <div
        style={{
          fontSize: 9,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: isDark ? colors.slate[400] : colors.slate[500],
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 700,
          fontFamily: "'JetBrains Mono', monospace",
          color: isDark ? colors.slate[100] : colors.slate[800],
          lineHeight: 1.2,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function RenderedBody({ body, search, isDark }: { body: string; search: string; isDark: boolean }) {
  // Highlighting search matches in rendered Markdown is tricky without a
  // tree-walk; we keep it simple — when search is non-empty, append a small
  // hint row above the body and let the browser's Cmd+F drive precise
  // navigation. For raw mode the highlight is inline (see RawBody).
  return (
    <div className="instruction-detail-body">
      {search.trim().length > 0 && (
        <div
          style={{
            marginBottom: 14,
            padding: '8px 12px',
            background: isDark ? colors.slate[800] : colors.slate[50],
            border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
            borderRadius: 6,
            fontSize: 12,
            color: isDark ? colors.slate[300] : colors.slate[600],
          }}
        >
          Use your browser's Find (<kbd>Ctrl/Cmd + F</kbd>) to locate "{search}" in the rendered view, or switch to Raw for inline highlighting.
        </div>
      )}
      <div
        className={isDark ? 'markdown-body markdown-body-dark' : 'markdown-body'}
        style={{
          fontSize: 14,
          lineHeight: 1.65,
          color: isDark ? colors.slate[100] : colors.slate[800],
        }}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeHighlight]}
          components={{
            h1: ({ children, ...rest }) => (
              <h1 id={slugify(stringifyChildren(children))} {...rest}>
                {children}
              </h1>
            ),
            h2: ({ children, ...rest }) => (
              <h2 id={slugify(stringifyChildren(children))} {...rest}>
                {children}
              </h2>
            ),
            h3: ({ children, ...rest }) => (
              <h3 id={slugify(stringifyChildren(children))} {...rest}>
                {children}
              </h3>
            ),
          }}
        >
          {body}
        </ReactMarkdown>
      </div>
    </div>
  );
}

function RawBody({ body, search, isDark }: { body: string; search: string; isDark: boolean }) {
  const lines = body.split('\n');
  const lineNumWidth = String(lines.length).length;
  const q = search.trim();
  const qLower = q.toLowerCase();

  return (
    <div
      style={{
        background: isDark ? colors.slate[900] : colors.slate[50],
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        borderRadius: 10,
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        fontSize: 12.5,
        lineHeight: 1.65,
        overflow: 'hidden',
      }}
    >
      {lines.map((raw, i) => {
        const display = renderHighlight(raw, qLower, isDark);
        const isHit = q.length > 0 && raw.toLowerCase().includes(qLower);
        return (
          <div
            key={i}
            style={{
              display: 'grid',
              gridTemplateColumns: `${lineNumWidth + 1}ch 1fr`,
              gap: 12,
              padding: '0 14px',
              background: isHit
                ? (isDark ? colors.amber[700] + '22' : colors.amber[100])
                : 'transparent',
            }}
          >
            <span
              style={{
                userSelect: 'none',
                color: isDark ? colors.slate[500] : colors.slate[400],
                textAlign: 'right',
              }}
            >
              {i + 1}
            </span>
            <span
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                color: isDark ? colors.slate[100] : colors.slate[800],
              }}
            >
              {display.length === 0 ? ' ' : display}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function renderHighlight(line: string, qLower: string, isDark: boolean): React.ReactNode[] | string {
  if (!qLower) return [line];
  const lower = line.toLowerCase();
  const out: React.ReactNode[] = [];
  let cursor = 0;
  while (cursor < line.length) {
    const idx = lower.indexOf(qLower, cursor);
    if (idx === -1) {
      out.push(line.slice(cursor));
      break;
    }
    if (idx > cursor) out.push(line.slice(cursor, idx));
    const matched = line.slice(idx, idx + qLower.length);
    out.push(
      <mark
        key={`m-${idx}`}
        style={{
          background: isDark ? colors.amber[600] : colors.amber[100],
          color: isDark ? '#000' : colors.amber[700],
          padding: 0,
          borderRadius: 2,
        }}
      >
        {matched}
      </mark>,
    );
    cursor = idx + qLower.length;
  }
  return out;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

function stringifyChildren(children: React.ReactNode): string {
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) return children.map(stringifyChildren).join('');
  if (children && typeof children === 'object' && 'props' in (children as { props?: { children?: unknown } })) {
    const c = (children as { props?: { children?: React.ReactNode } }).props?.children;
    if (c != null) return stringifyChildren(c);
  }
  return '';
}

function cssEscape(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(value);
  return value.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
}
